/* eslint-disable react/prop-types */
import { useMemo } from "react";
import { Field, reduxForm, formValueSelector } from "redux-form";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import i18n from "i18next";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import FormField from "components/FormField";
import FormSelector from "components/FormSelector";
import MDTypography from "components/MDTypography";
import { useTranslation } from "react-i18next";
import { buildClientReservationSelectorOptions } from "layouts/clients/clientReservationsUtils";
import serviceUtils from "../project/components/servicesConfig/serviceUtils";

const validate = (values) => {
  const errors = {};
  if (!values.title) errors.title = i18n.t("Required");
  if (!values.clientReservationId) errors.clientReservationId = i18n.t("Required");
  if (!values.status) errors.status = i18n.t("Required");
  return errors;
};

function SupportCaseForm({
  handleSubmit,
  onSubmit,
  actionType,
  projects,
  apartments,
  clients,
  clientReservations,
  selectedClientReservationId,
  initialValues,
}) {
  const { t } = useTranslation();
  const { getClientReservationsDerived, formatTimestampToProjectTimeZone } = serviceUtils;

  const { clientReservationsById, activeClientReservations } = useMemo(
    () => getClientReservationsDerived(clientReservations),
    [clientReservations]
  );

  const resolvedClientReservationId =
    selectedClientReservationId || initialValues?.clientReservationId || "";

  const selectedClientReservation = clientReservationsById[resolvedClientReservationId] || null;

  const selectableReservations = (() => {
    const selectedId = selectedClientReservation?.uid;
    const existsInActive = activeClientReservations.some(
      (reservation) => reservation?.uid === selectedId
    );

    if (selectedId && !existsInActive) {
      return [...activeClientReservations, selectedClientReservation];
    }

    return activeClientReservations;
  })();

  const selectableReservationOptions = useMemo(
    () =>
      buildClientReservationSelectorOptions({
        reservations: selectableReservations,
        clients,
        apartments,
        formatTimestamp: formatTimestampToProjectTimeZone,
        getTimeZone: (reservation) => projects?.[reservation?.projectId]?.timeZone || "UTC",
      }),
    [selectableReservations, clients, apartments, projects, formatTimestampToProjectTimeZone]
  );

  const selectedApartmentId = selectedClientReservation?.apartmentId || "";
  const selectedProjectId = selectedClientReservation?.projectId || "";

  const apartmentName = apartments[selectedApartmentId]?.name || selectedApartmentId || "";
  const projectName = projects[selectedProjectId]?.name || selectedProjectId || "";

  const submitData = (data) => {
    const clientReservationId = data?.clientReservationId || resolvedClientReservationId;

    const selectedReservation =
      clientReservationsById[clientReservationId] || selectedClientReservation;
    if (!selectedReservation) return;

    onSubmit({
      ...data,
      clientReservationId,
      projectId: selectedReservation?.projectId,
      apartmentId: selectedReservation?.apartmentId,
      clientUserId: selectedReservation?.clientId,
    });
  };

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(submitData)}>
        <Grid container justifyContent="center" alignItems="center">
          <Card id="basic-info" sx={{ overflow: "visible" }}>
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("support")}
            </MDBox>

            <MDBox pt={3} pb={3} px={3}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Field name="title" component={FormField} label={t("name")} />
                </Grid>

                {/* CLIENT RESERVATION */}
                <Grid size={{ xs: 12 }}>
                  <Field
                    name="clientReservationId"
                    component={FormSelector}
                    values={selectableReservationOptions.values}
                    names={selectableReservationOptions.names}
                    label={t("clientReservation")}
                    disabled={actionType === "edit"}
                  />
                </Grid>

                {/* PROJECT */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    {t("project")}
                  </MDTypography>
                  <MDTypography variant="body2" mt={1}>
                    {projectName}
                  </MDTypography>
                  <Field name="projectId" component="input" type="hidden" />
                  <Field name="clientUserId" component="input" type="hidden" />
                </Grid>

                {/* APARTMENT */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    {t("apartment")}
                  </MDTypography>
                  <MDTypography variant="body2" mt={1}>
                    {apartmentName}
                  </MDTypography>
                  <Field name="apartmentId" component="input" type="hidden" />
                </Grid>

                {/* STATUS */}
                <Grid size={{ xs: 12 }}>
                  <Field
                    name="status"
                    component={FormSelector}
                    values={["pending", "in progress", "solved"]}
                    names={[
                      { uid: "pending", name: t("pending") },
                      { uid: "in progress", name: t("supportInProgress") },
                      { uid: "solved", name: t("supportCompleted") },
                    ]}
                    label={t("state")}
                  />
                </Grid>

                <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
                  <MDButton type="submit" variant="gradient" color="dark" size="small">
                    {t("Save")}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

SupportCaseForm.defaultProps = {
  projects: {},
  apartments: {},
  clients: {},
  clientReservations: {},
  selectedClientReservationId: "",
  initialValues: {},
};

SupportCaseForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  projects: PropTypes.object,
  apartments: PropTypes.object,
  clients: PropTypes.object,
  clientReservations: PropTypes.object,
  selectedClientReservationId: PropTypes.string,
  initialValues: PropTypes.object,
};

const selector = formValueSelector("SupportCaseForm");

const mapStateToProps = (state) => {
  const {
    projectsReducer = {},
    apartmentsReducer = {},
    clientsReducer = {},
    clientReservationsReducer = {},
    mainUserReducer = {},
  } = state;

  const isProjectManager = mainUserReducer?.isProjectManager;
  const userProjects = mainUserReducer?.projects || {};

  let clientReservations = clientReservationsReducer?.clientReservations || {};

  if (isProjectManager) {
    clientReservations = Object.fromEntries(
      Object.entries(clientReservations)
        .map(([clientId, reservationsByClient]) => [
          clientId,
          Object.fromEntries(
            Object.entries(reservationsByClient || {}).filter(
              ([, reservation]) => userProjects?.[reservation?.projectId]
            )
          ),
        ])
        .filter(([, reservationsByClient]) => Object.keys(reservationsByClient).length > 0)
    );
  }

  return {
    projects: projectsReducer || {},
    apartments: apartmentsReducer || {},
    clients: clientsReducer,
    clientReservations,
    selectedClientReservationId: selector(state, "clientReservationId"),
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: "SupportCaseForm",
    validate,
    enableReinitialize: true,
  })(SupportCaseForm)
);
